import dayjs from "dayjs";
import React, { useEffect } from "react";

import { SHORT_DATE_FORMAT, TAB, TOOL_TEMPLATE } from "../constant/common";
import {
  AmountInvestedSelectContext,
  AmountInvestedTabContentContext,
  SharesPurchasedSelectContext,
} from "../context/AmountAndShareSelectContext";
import { useCompanySettings, useSelectedInstrument, useTemplateSetting } from "../hooks/useCompanySettings";
import useForm from "../hooks/useForm";
import { useInstrument } from "../hooks/useInstruments";
import { useAmountInvested, useSharesPurchased } from "../hooks/useTrades";
import i18n from "../locale/i18n";
import { classNames, getDateParam, pickBy } from "../utils";
import {
  QuadrataAmountInvestedSelection,
  QuadrataSharesPurchasedSelection,
  VictorAmountInvestedSelection,
  VictorSharesPurchasedSelection,
} from "./AmountAndShareSelection";
import DataUnavailableIPO from "./DataUnavailableIPO/DataUnavailableIPO";
import { VictorSquareCheckbox } from "./ui/Checkbox";
import { TabList, Tab, TabPanels, TabPanel, Tabs } from "@euroland/react-tablist";
import AmountInvestedTabContent from "./AmountInvestedTabContent";
import SharePurchasedTabContent from "./SharePurchasedTabContent";
import AverageBasedCalculatorButton from "../feat-average-based-calculator/components/AverageBasedCalculatorButton/AverageBasedCalculatorButton";
import { getInstrumentSharePrice } from "../services";
import { resolveMinimumSharePrice } from "../utils/amountInvestedValidation";

export default function AmountSharesSelectionTabs({ tab, setTab }) {
  const [
    {
      id,
      enabledDividendOption: enabledDividendOpt,
      limitStartingData,
      limitInvestmentStartDate: limitInvestmentStartDte,
    },
  ] = useSelectedInstrument();
  const [{ selectedInstrument }] = useInstrument();
  const [{ query, userEntryAmountInvested }, { getAmountInvested }] = useAmountInvested();
  const [, { getSharesPurchased }] = useSharesPurchased();
  const companySettings = useCompanySettings();

  const template = useTemplateSetting();
  const amountInvestedFormState = useForm(
    {
      startDate: null,
      endDate: null,
      amountInvested: "",
      currency: "",
      ownSharePrice: 0,
      dividendChecked: false,
    },
    { amountInvested: "required" }
  );

  const sharesPurchasedFormState = useForm(
    {
      startDate: null,
      endDate: null,
      shareNumber: "",
      currency: "",
      ownSharePrice: 0,
      dividendChecked: false,
    },
    { shareNumber: "required" }
  );

  const limitStartingDate = dayjs(limitStartingData);
  const limitInvestmentStartDate = dayjs(limitInvestmentStartDte);
  let endDate;
  let startDate;
  if (selectedInstrument.lastUpdatedDate) {
    //const lastUpdatedDate = dayjs(selectedInstrument.lastUpdatedDate);
    const lastUpdatedDate = dayjs();

    endDate = lastUpdatedDate;

    startDate = endDate.subtract(1, "year");
    if (startDate.isBefore(limitStartingDate, "date")) {
      startDate = limitStartingDate;
    }
    if (startDate.isAfter(limitInvestmentStartDate, "date")) {
      startDate = limitInvestmentStartDate;
    }
  }

  const enabledCalculatorAverageBased = !!companySettings.enabledCalculatorAverageBased;

  let enabledDividendOption = !!companySettings.enabledDividendOption;
  if (typeof enabledDividendOpt === "boolean") {
    enabledDividendOption = enabledDividendOpt;
  }

  useEffect(() => {
    if (!query.instrumentId || !selectedInstrument.startingDate) return;
    let cancelled = false;
    const params = { ...query };

    const getAmountInvestedData = (params) => {
      params.instrumentId = selectedInstrument.instrumentId;
      params.dividendChecked = enabledDividendOption && params.dividendChecked;
      params.startDate = getDateParam(startDate);
      params.endDate = getDateParam(endDate);
      switch (tab) {
        case TAB.AMOUNT_INVESTED:
          getAmountInvested(pickBy(params));
          break;
        case TAB.SHARES_PURCHASED:
          getSharesPurchased(pickBy(params));
          break;
        default:
          break;
      }
    };
    const fetchMinimumPriceThenProcessAmountInvested = async () => {
      try {
        const response = await getInstrumentSharePrice({
          instrumentId: selectedInstrument.instrumentId,
          startDate: getDateParam(startDate),
          endDate: getDateParam(endDate),
          currency:  params.currency || "",
        });
        if (cancelled) return;
        const data = response?.data?.instrumentSharePrice;
        if (data) {
          params.amountInvested = Math.max(userEntryAmountInvested, resolveMinimumSharePrice(data));
        }
      } finally {
        if(!cancelled) {
          getAmountInvestedData(params);
        }
      }
    };
    fetchMinimumPriceThenProcessAmountInvested();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstrument.instrumentId]);

  const startDateString = startDate?.format(SHORT_DATE_FORMAT);
  const endDateString = endDate?.format(SHORT_DATE_FORMAT);
  // set initial value when have limit dates
  useEffect(() => {
    if (!sharesPurchasedFormState.getFieldValue("startDate") && startDate) {
      sharesPurchasedFormState.setFieldValue("startDate", startDate.toDate());
    }

    if (!amountInvestedFormState.getFieldValue("startDate") && startDate) {
      amountInvestedFormState.setFieldValue("startDate", startDate.toDate());
    }
    return () => {
      sharesPurchasedFormState.setFieldValue("startDate", null);
      amountInvestedFormState.setFieldValue("startDate", null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, tab, startDateString]);

  useEffect(() => {
    if (!sharesPurchasedFormState.getFieldValue("endDate") && endDate) {
      sharesPurchasedFormState.setFieldValue("endDate", endDate.toDate());
    }
   
    if (!amountInvestedFormState.getFieldValue("endDate") && endDate) {
      amountInvestedFormState.setFieldValue("endDate", endDate.toDate());
    }
    return () => {
      sharesPurchasedFormState.setFieldValue("endDate", null);
      amountInvestedFormState.setFieldValue("endDate", null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, tab, endDateString]);

  const showDividendsReinvested = template === TOOL_TEMPLATE.victor && enabledDividendOpt;

  const handleSelectVictorSquareCheckbox = () => {
    switch (tab) {
      case TAB.AMOUNT_INVESTED:
        amountInvestedFormState.setFieldValue(
          "dividendChecked",
          !amountInvestedFormState.getFieldValue("dividendChecked")
        );
        break;
      case TAB.SHARES_PURCHASED:
        sharesPurchasedFormState.setFieldValue(
          "dividendChecked",
          !sharesPurchasedFormState.getFieldValue("dividendChecked")
        );
        break;
      default:
        break;
    }
  };

  const getValueVictorSquareCheckbox = () => {
    switch (tab) {
      case TAB.AMOUNT_INVESTED:
        return amountInvestedFormState.getFieldValue("dividendChecked");
      case TAB.SHARES_PURCHASED:
        return sharesPurchasedFormState.getFieldValue("dividendChecked");
      default:
        break;
    }
  };

  if (!selectedInstrument.instrumentId) return null;

  return (
    <Tabs activeTab={tab} onChangeTab={setTab}>
      <div
        className={classNames("switch--control", { "show-dividends-reinvested": showDividendsReinvested })}
      >
        <div className="switch--control__container">
          <TabList ariaLabel={i18n.translate("investmentDetailTabs")} className="tab-list">
            <Tab
              value={TAB.AMOUNT_INVESTED}
              className="switch--control__amount-btn button"
              activeClassName="button--primary"
            >
              {i18n.translate("amountInvested")}
            </Tab>
            <Tab
              value={TAB.SHARES_PURCHASED}
              className="switch--control__shares-btn button"
              activeClassName="button--primary"
            >
              {i18n.translate("sharesPurchased")}
            </Tab>
          </TabList>
          {showDividendsReinvested && (
            <VictorSquareCheckbox
              className="victor-selection__ratio"
              selected={getValueVictorSquareCheckbox()}
              onSelect={handleSelectVictorSquareCheckbox}
            />
          )}
          {enabledCalculatorAverageBased && <AverageBasedCalculatorButton />}
        </div>
      </div>
      {!!selectedInstrument.startingDate ? (
        <TabPanels>
          <TabPanel forTab={TAB.AMOUNT_INVESTED}>
            <div
              className={classNames(
                { "quadrata-selection": template === TOOL_TEMPLATE.quadrata },
                { "victor-selection": template === TOOL_TEMPLATE.victor }
              )}
            >
              <AmountInvestedSelectContext.Provider
                value={{ ...amountInvestedFormState, tab, enabledDividendOption }}
              >
                {template === TOOL_TEMPLATE.quadrata && <QuadrataAmountInvestedSelection />}
                {template === TOOL_TEMPLATE.victor && <VictorAmountInvestedSelection />}
              </AmountInvestedSelectContext.Provider>
            </div>
            <AmountInvestedTabContentContext.Provider value={{ hidden: tab !== TAB.AMOUNT_INVESTED }}>
              <AmountInvestedTabContent />
            </AmountInvestedTabContentContext.Provider>
          </TabPanel>
          <TabPanel forTab={TAB.SHARES_PURCHASED}>
            <div
              className={classNames(
                { "quadrata-selection": template === TOOL_TEMPLATE.quadrata },
                { "victor-selection": template === TOOL_TEMPLATE.victor }
              )}
            >
              <SharesPurchasedSelectContext.Provider
                value={{ ...sharesPurchasedFormState, tab, enabledDividendOption }}
              >
                {template === TOOL_TEMPLATE.quadrata && <QuadrataSharesPurchasedSelection />}
                {template === TOOL_TEMPLATE.victor && <VictorSharesPurchasedSelection />}
              </SharesPurchasedSelectContext.Provider>
            </div>
            <AmountInvestedTabContentContext.Provider value={{ hidden: tab !== TAB.SHARES_PURCHASED }}>
              <SharePurchasedTabContent />
            </AmountInvestedTabContentContext.Provider>
          </TabPanel>
        </TabPanels>
      ) : (
        <DataUnavailableIPO />
      )}
    </Tabs>
  );
}
